import { useCurrentUserPermissions } from '../api/permissions';
import Permission from './permissions';
import { hasPermissionCategory } from './hasPermissionCategory';

export default function useHasPermissions() {
  const { isLoading, isError, data, permissions: userPermissions } = useCurrentUserPermissions();

  // Ready once permissions have actually loaded, not merely "not fetching". After
  // an org switch clears the cache the query is idle with no data; treating that
  // as ready would gate the user out before the new org's permissions arrive.
  const isReady = computed(() => data.value !== undefined);

  // checkPermissions helps check if an array of user permissions satisfies the requirements of 1 OR more actions.
  function checkPermissions(actionsToCheck: Permission[], permissions: string[]) {
    for (const permission of permissions) {
      for (const action of actionsToCheck) {
        // fast path for catch-all permissions
        if (permission === '*') {
          return true;
        }

        // exact match
        if (permission === action) {
          return true;
        }

        // check for user wildcard permission match
        const wildcardIndex = permission.indexOf('*');

        if (wildcardIndex !== -1) {
          const prefix = permission.substring(0, wildcardIndex);
          const suffix = permission.substring(wildcardIndex + 1, permission.length);

          if (action.startsWith(prefix) && action.endsWith(suffix)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // hasAnyOfPermissions returns `true` if at least one permission satisfies
  // one of the actions required. If no actions are satisfied by the
  // users permissions, `false` is returned.
  // uses the current user permissions.
  function hasAnyOfPermissions(...actions: Permission[]) {
    if (isLoading.value) {
      return false;
    }

    return checkPermissions(actions, userPermissions.value);
  }

  const hasClientPermissions = computed(() =>
    hasPermissionCategory(userPermissions.value, 'clients'),
  );
  const hasSupplierPermissions = computed(() =>
    hasPermissionCategory(userPermissions.value, 'suppliers'),
  );
  const hasFederatedPermissions = computed(() =>
    hasPermissionCategory(userPermissions.value, 'federated'),
  );

  // Defined for TypeScript
  function override(_permissions: string) {
    throw new Error('This is only available in the mocked version of this file');
  }

  return {
    isReady,
    isLoading,
    isError,
    checkPermissions,
    hasAnyOfPermissions,
    hasClientPermissions,
    hasSupplierPermissions,
    hasFederatedPermissions,
    Permission,
    override,
  };
}
